import {inject, Injectable} from '@angular/core';
import {catchError, map, Observable, throwError} from 'rxjs';
import {ApiService} from "../../shared/services/api.service";
import {DefaultResponse} from "../../../interfaces/responses";
import {InspectionModel} from "../../../models/inspection.model";
import {HttpParams} from "@angular/common/http";

export interface DashboardFilterOptions {
    page: number;
    limit: number;
    ffsRecommendationCodes?: number[];
    siteIds?: string[];
    unityIds?: string[];
}

export interface DashboardResponse {
    data: any[];
    total: number;
    page: number;
    limit: number;
}

@Injectable({
    providedIn: 'root'
})
export class InspectionService {
    private readonly api = inject(ApiService);
    private readonly resource = 'inspections';

    getInspections(): Observable<DefaultResponse<InspectionModel[]>> {
        return this.api.get<DefaultResponse<InspectionModel[]>>(this.resource);
    }

    getInspectionsByEquipmentPoint(pointId: number, params: any = {}): Observable<DefaultResponse<InspectionModel[]>> {
        let url = `${this.resource}/points/${pointId}/inspections`;

        // Construire la chaîne de requête pour les paramètres
        const queryParams = Object.keys(params)
            .filter(key => params[key] !== null && params[key] !== undefined)
            .map(key => `${key}=${encodeURIComponent(params[key])}`)
            .join('&');

        if (queryParams) {
            url += `?${queryParams}`;
        }

        return this.api.get<DefaultResponse<InspectionModel[]>>(url);
    }

    getLastInspections(pointId: number, inspectionDate: string, limit = 4): Observable<DefaultResponse<InspectionModel[]>> {
        return this.api.get<DefaultResponse<InspectionModel[]>>(
            `${this.resource}/last-inspections/${inspectionDate}?pointId=${pointId}&limit=${limit}`
        );
    }

    createInspection(pointId: number, data: any): Observable<any> {
        const payload = {...data, pointId};
        return this.api.post(`${this.resource}`, payload);
    }

    updateInspection(inspectionId: string, data: any): Observable<any> {
        const payload = {...data, pointId: data.id};
        return this.api.patch(`${this.resource}/${inspectionId}`, payload);
    }

    getInspectionById(inspectionId: string): Observable<DefaultResponse<InspectionModel>> {
        return this.api.get<DefaultResponse<InspectionModel>>(`${this.resource}/${inspectionId}`);
    }

    forceRecalculate(inspectionId: string): Observable<any> {
        return this.api.post(`${this.resource}/${inspectionId}/recalculate`, {});
    }

    checkInspectionExists(pointId: number, inspectionDate: string | null): Observable<{
        exists: boolean,
        message: string
    }> {
        return this.api.get(`${this.resource}/check/${pointId}/${inspectionDate}`);
    }

    /**
     * Updates the forced corrosion rate for an inspection
     * @param inspectionId The ID of the inspection to update
     * @param forcedCR The forced corrosion rate value
     * @returns Observable with API response
     */
    updateInspectionForcedCR(inspectionId: string, forcedCR: number): Observable<any> {
        return this.api.patch(`${this.resource}/${inspectionId}/forced-cr`, {forcedCR})
            .pipe(
                catchError(error => {
                    console.error('Error updating forced CR:', error);
                    return throwError(() => error);
                })
            );
    }


    /**
     * Récupère les inspections terminées pour un point d'équipement avec pagination
     * @param pointId ID du point d'équipement
     * @param page Numéro de page (commence à 0)
     * @param pageSize Taille de la page
     * @returns Observable contenant les inspections et le nombre total
     */
    getCompletedInspectionsByPoint(pointId: number, page: number = 0, pageSize: number = 4): Observable<any> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString())
            .set('status', 'completed');

        return this.api.get<any>(`${this.resource}/points/${pointId}/inspections/paginate`, { params })
            .pipe(
                map(response => ({
                    inspections: response.data,
                    total: response.count
                })),
                catchError(error => {
                    console.error('Error fetching inspections:', error);
                    return throwError(() => error);
                })
            );
    }

    getInspectionsDashboard(options: DashboardFilterOptions): Observable<DashboardResponse> {
        let params = new HttpParams()
            .set('page', options.page.toString())
            .set('limit', options.limit.toString());

        if (options.ffsRecommendationCodes?.length) {
            params = params.set('ffsRecommendationCodes', JSON.stringify(options.ffsRecommendationCodes));
        }
        
        if (options.siteIds?.length) {
            params = params.set('siteIds', JSON.stringify(options.siteIds));
        }
        
        if (options.unityIds?.length) {
            params = params.set('unityIds', JSON.stringify(options.unityIds));
        }

        return this.api.get<DashboardResponse>(`${this.resource}/dashboard`, {params});
    }
}
